import { motion } from "framer-motion";
import {
	AlertCircle,
	CheckCircle2,
	ClipboardCopy,
	Columns3,
	Copy,
	Eye,
	Globe,
	LayoutList,
	Search,
	Shield,
} from "lucide-react";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { KanbanBoard } from "../components/kanban/KanbanBoard";
import LoadingSpinner from "../components/LoadingSpinner";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../components/ui/select";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "../components/ui/table";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "../components/ui/tooltip";
import { useWallets } from "../context/WalletContext";
import { INTEGRATION_STATUSES } from "../lib/constants";
import { getIntegrationBadgeVariant } from "../lib/statusBadge";
import { cn } from "../lib/utils";
import type { IntegrationStatus, Wallet } from "../types";

function isConfigComplete(wallet: Wallet): boolean {
	const custodialType =
		wallet.production_custodial_type ?? wallet.custodial_type;

	if (custodialType === "Custodial") {
		return !!(
			wallet.production_home_domain && wallet.production_funding_address
		);
	}
	if (custodialType === "Non-Custodial") {
		return !!wallet.production_toml_file_domain;
	}
	// Both / Hybrid
	return !!(
		wallet.production_home_domain &&
		wallet.production_funding_address &&
		wallet.production_toml_file_domain
	);
}

function isProductionWhitelisted(wallet: Wallet): boolean {
	return (
		wallet.is_whitelisted &&
		(wallet.whitelist_environments ?? []).includes("Production")
	);
}

function truncateAddress(addr: string | undefined, chars = 6): string {
	if (!addr) return "—";
	if (addr.length <= chars * 2 + 3) return addr;
	return `${addr.slice(0, chars)}...${addr.slice(-chars)}`;
}

function truncateDomain(domain: string | undefined, maxLen = 24): string {
	if (!domain) return "—";
	if (domain.length <= maxLen) return domain;
	return `${domain.slice(0, maxLen)}...`;
}

async function copyToClipboard(text: string, label: string) {
	try {
		await navigator.clipboard.writeText(text);
		toast.success(`${label} copied`);
	} catch {
		toast.error("Failed to copy");
	}
}

function collectAllAddresses(wallet: Wallet): string {
	const lines: string[] = [];
	if (wallet.production_home_domain)
		lines.push(`Home Domain: ${wallet.production_home_domain}`);
	if (wallet.production_funding_address)
		lines.push(`Funding: ${wallet.production_funding_address}`);
	if (wallet.production_deposit_address)
		lines.push(`Deposit: ${wallet.production_deposit_address}`);
	if (wallet.production_auth_address)
		lines.push(`Auth: ${wallet.production_auth_address}`);
	if (wallet.production_toml_file_domain)
		lines.push(`TOML Domain: ${wallet.production_toml_file_domain}`);
	return lines.join("\n");
}

const CopyButton: React.FC<{ value?: string; label: string }> = ({
	value,
	label,
}) => {
	if (!value) return null;
	return (
		<button
			type="button"
			onClick={(e) => {
				e.stopPropagation();
				copyToClipboard(value, label);
			}}
			className="ml-1 inline-flex items-center justify-center w-5 h-5 rounded text-[#9a9a9a] dark:text-gray-400 hover:text-(--color-accent) hover:bg-(--color-muted) transition-colors opacity-0 group-hover/row:opacity-100"
			title={`Copy ${label}`}
		>
			<Copy size={12} />
		</button>
	);
};

const StellarAdmin: React.FC = () => {
	const navigate = useNavigate();
	const { wallets, loading } = useWallets();

	const [searchTerm, setSearchTerm] = useState("");
	const [statusFilter, setStatusFilter] = useState<IntegrationStatus | "All">(
		"All",
	);
	const [custodialFilter, setCustodialFilter] = useState<string>("All");
	const [configFilter, setConfigFilter] = useState<string>("All");
	const [whitelistFilter, setWhitelistFilter] = useState<string>("All");
	const [viewMode, setViewMode] = useState<"table" | "kanban">(() => {
		return (
			(localStorage.getItem("stellar-admin-view-mode") as
				| "table"
				| "kanban"
				| null) ?? "table"
		);
	});

	useEffect(() => {
		localStorage.setItem("stellar-admin-view-mode", viewMode);
	}, [viewMode]);

	const stellarWallets = useMemo(
		() => wallets.filter((w) => w.chain === "Stellar"),
		[wallets],
	);

	const filteredWallets = useMemo(() => {
		return stellarWallets.filter((w) => {
			if (
				searchTerm &&
				!(w.wallet_name ?? "")
					.toLowerCase()
					.includes(searchTerm.toLowerCase()) &&
				!(w.company_name ?? "").toLowerCase().includes(searchTerm.toLowerCase())
			) {
				return false;
			}
			if (statusFilter !== "All" && w.integration_status !== statusFilter) {
				return false;
			}
			if (custodialFilter !== "All" && w.custodial_type !== custodialFilter) {
				return false;
			}
			if (configFilter === "Complete" && !isConfigComplete(w)) return false;
			if (configFilter === "Incomplete" && isConfigComplete(w)) return false;
			if (whitelistFilter === "Whitelisted" && !isProductionWhitelisted(w))
				return false;
			if (whitelistFilter === "Not Whitelisted" && isProductionWhitelisted(w))
				return false;
			return true;
		});
	}, [
		stellarWallets,
		searchTerm,
		statusFilter,
		custodialFilter,
		configFilter,
		whitelistFilter,
	]);

	const stats = useMemo(() => {
		const complete = stellarWallets.filter(isConfigComplete).length;
		const whitelisted = stellarWallets.filter(isProductionWhitelisted).length;
		return {
			total: stellarWallets.length,
			complete,
			incomplete: stellarWallets.length - complete,
			whitelisted,
		};
	}, [stellarWallets]);

	if (loading) {
		return <LoadingSpinner size="lg" message="Loading wallets..." centered />;
	}

	const statCards = [
		{
			value: stats.total,
			label: "Stellar Wallets",
			icon: Globe,
			color: "text-(--color-accent)",
			bg: "bg-(--color-accent-muted)",
			border: "border-(--color-accent-border)",
		},
		{
			value: stats.complete,
			label: "Config Complete",
			icon: CheckCircle2,
			color: "text-[#00E6C3]",
			bg: "bg-[#00E6C3]/12",
			border: "border-[#00E6C3]/35",
		},
		{
			value: stats.incomplete,
			label: "Config Incomplete",
			icon: AlertCircle,
			color: "text-[#F59E0B]",
			bg: "bg-[#F59E0B]/12",
			border: "border-[#F59E0B]/35",
		},
		{
			value: stats.whitelisted,
			label: "Prod Whitelisted",
			icon: Shield,
			color: "text-[#3B82F6]",
			bg: "bg-[#3B82F6]/12",
			border: "border-[#3B82F6]/35",
		},
	];

	return (
		<div className="min-h-[calc(100vh-56px)] overflow-x-hidden bg-(--color-background) px-4 pb-8 pt-20">
			<div className="mx-auto max-w-[1400px]">
				{/* Header */}
				<div className="my-6">
					<div className="mb-5 flex flex-wrap items-end justify-between gap-4">
						<div>
							<h1 className="text-3xl md:text-4xl font-bold text-(--color-foreground) mb-2 normal-case">
								Stellar Wallets
							</h1>
							<p className="text-(--color-muted-foreground) text-base">
								Manage and monitor Stellar wallet configurations
							</p>
						</div>
						<div className="flex items-center rounded-xl border border-(--color-border) p-0.5">
							<Button
								variant={viewMode === "table" ? "subtle" : "ghost"}
								size="icon"
								className="h-7 w-7"
								onClick={() => setViewMode("table")}
								title="Table view"
							>
								<LayoutList size={15} />
							</Button>
							<Button
								variant={viewMode === "kanban" ? "subtle" : "ghost"}
								size="icon"
								className="h-7 w-7"
								onClick={() => setViewMode("kanban")}
								title="Board view"
							>
								<Columns3 size={15} />
							</Button>
						</div>
					</div>

					{/* Stat Cards */}
					<div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-5">
						{statCards.map(
							({ value, label, icon: Icon, color, bg, border }, i) => (
								<motion.div
									key={label}
									initial={{ opacity: 0, y: 24 }}
									animate={{ opacity: 1, y: 0 }}
									transition={{ delay: i * 0.1, duration: 0.5 }}
								>
									<div
										className={cn(
											"w-full rounded-xl border p-3 text-left",
											`bg-(--color-card) border-(--color-border)`,
										)}
									>
										<div className="flex items-center justify-between mb-1.5">
											<div className={cn("p-1 rounded-lg border", bg, border)}>
												<Icon className={cn("w-3.5 h-3.5", color)} />
											</div>
										</div>
										<p className="text-xl font-bold text-(--color-foreground) mb-0.5">
											{value}
										</p>
										<p className="text-[9px] font-medium text-(--color-muted-foreground) uppercase tracking-wider">
											{label}
										</p>
									</div>
								</motion.div>
							),
						)}
					</div>
				</div>

				{/* Filters */}
				<div className="flex flex-wrap items-center gap-2.5 mb-4">
					<div className="relative flex-1 min-w-[200px] max-w-sm">
						<Search
							size={15}
							className="absolute left-2.5 top-1/2 -translate-y-1/2 text-(--color-muted-foreground)"
						/>
						<Input
							placeholder="Search wallet or company..."
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							className="pl-8 h-9"
						/>
					</div>

					<Select
						value={statusFilter}
						onValueChange={(v) =>
							setStatusFilter(v as IntegrationStatus | "All")
						}
					>
						<SelectTrigger className="w-[160px]">
							<SelectValue placeholder="Status" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="All">All Statuses</SelectItem>
							{INTEGRATION_STATUSES.map((s) => (
								<SelectItem key={s} value={s}>
									{s}
								</SelectItem>
							))}
						</SelectContent>
					</Select>

					<Select value={custodialFilter} onValueChange={setCustodialFilter}>
						<SelectTrigger className="w-[150px]">
							<SelectValue placeholder="Custodial Type" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="All">All Types</SelectItem>
							<SelectItem value="Custodial">Custodial</SelectItem>
							<SelectItem value="Non-Custodial">Non-Custodial</SelectItem>
						</SelectContent>
					</Select>

					<Select value={configFilter} onValueChange={setConfigFilter}>
						<SelectTrigger className="w-[140px]">
							<SelectValue placeholder="Config" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="All">All Configs</SelectItem>
							<SelectItem value="Complete">Complete</SelectItem>
							<SelectItem value="Incomplete">Incomplete</SelectItem>
						</SelectContent>
					</Select>

					<Select value={whitelistFilter} onValueChange={setWhitelistFilter}>
						<SelectTrigger className="w-[160px]">
							<SelectValue placeholder="Whitelist" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="All">All Whitelist</SelectItem>
							<SelectItem value="Whitelisted">Whitelisted</SelectItem>
							<SelectItem value="Not Whitelisted">Not Whitelisted</SelectItem>
						</SelectContent>
					</Select>
				</div>

				{/* Table / Kanban */}
				{viewMode === "kanban" ? (
					<KanbanBoard wallets={filteredWallets} />
				) : (
					<div className="rounded-xl border border-(--color-border) bg-(--color-card) overflow-hidden">
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Wallet Name</TableHead>
									<TableHead>Company</TableHead>
									<TableHead>Type</TableHead>
									<TableHead>Status</TableHead>
									<TableHead>Home Domain</TableHead>
									<TableHead>Funding Address</TableHead>
									<TableHead>Deposit Address</TableHead>
									<TableHead>Auth Address</TableHead>
									<TableHead>TOML Domain</TableHead>
									<TableHead>Whitelist</TableHead>
									<TableHead>Config</TableHead>
									<TableHead className="text-right">Actions</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{filteredWallets.length === 0 ? (
									<TableRow>
										<TableCell
											colSpan={12}
											className="h-24 text-center text-(--color-muted-foreground)"
										>
											No Stellar wallets found.
										</TableCell>
									</TableRow>
								) : (
									filteredWallets.map((wallet) => (
										<TableRow
											key={wallet.id}
											className="group/row cursor-pointer"
											onClick={() => navigate(`/wallet/${wallet.id}`)}
										>
											<TableCell className="font-medium text-(--color-accent) whitespace-nowrap">
												{wallet.wallet_name}
											</TableCell>
											<TableCell className="whitespace-nowrap">
												{wallet.company_name}
											</TableCell>
											<TableCell>
												<Badge variant="secondary" className="text-xs">
													{wallet.custodial_type}
												</Badge>
											</TableCell>
											<TableCell>
												<Badge
													variant={getIntegrationBadgeVariant(
														wallet.integration_status,
													)}
													className="text-xs whitespace-nowrap"
												>
													{wallet.integration_status}
												</Badge>
											</TableCell>
											<TableCell className="font-mono text-xs whitespace-nowrap">
												<span title={wallet.production_home_domain}>
													{truncateDomain(wallet.production_home_domain)}
												</span>
												<CopyButton
													value={wallet.production_home_domain}
													label="Home Domain"
												/>
											</TableCell>
											<TableCell className="font-mono text-xs whitespace-nowrap">
												<span title={wallet.production_funding_address}>
													{truncateAddress(wallet.production_funding_address)}
												</span>
												<CopyButton
													value={wallet.production_funding_address}
													label="Funding Address"
												/>
											</TableCell>
											<TableCell className="font-mono text-xs whitespace-nowrap">
												<span title={wallet.production_deposit_address}>
													{truncateAddress(wallet.production_deposit_address)}
												</span>
												<CopyButton
													value={wallet.production_deposit_address}
													label="Deposit Address"
												/>
											</TableCell>
											<TableCell className="font-mono text-xs whitespace-nowrap">
												<span title={wallet.production_auth_address}>
													{truncateAddress(wallet.production_auth_address)}
												</span>
												<CopyButton
													value={wallet.production_auth_address}
													label="Auth Address"
												/>
											</TableCell>
											<TableCell className="font-mono text-xs whitespace-nowrap">
												{wallet.custodial_type === "Non-Custodial" ? (
													<>
														<span title={wallet.production_toml_file_domain}>
															{truncateDomain(
																wallet.production_toml_file_domain,
															)}
														</span>
														<CopyButton
															value={wallet.production_toml_file_domain}
															label="TOML Domain"
														/>
													</>
												) : (
													<span className="text-(--color-muted-foreground)">
														—
													</span>
												)}
											</TableCell>
											<TableCell>
												{isProductionWhitelisted(wallet) ? (
													<div className="flex flex-wrap gap-1">
														{(wallet.whitelist_environments ?? []).map(
															(env) => (
																<Badge
																	key={env}
																	variant="success"
																	className="text-[10px]"
																>
																	{env}
																</Badge>
															),
														)}
													</div>
												) : (
													<span className="text-(--color-muted-foreground) text-xs">
														Not whitelisted
													</span>
												)}
											</TableCell>
											<TableCell>
												{isConfigComplete(wallet) ? (
													<CheckCircle2 size={16} className="text-[#00E6C3]" />
												) : (
													<TooltipProvider>
														<Tooltip>
															<TooltipTrigger>
																<AlertCircle
																	size={16}
																	className="text-[#F59E0B]"
																/>
															</TooltipTrigger>
															<TooltipContent>
																<p>Missing required config fields</p>
															</TooltipContent>
														</Tooltip>
													</TooltipProvider>
												)}
											</TableCell>
											<TableCell className="text-right">
												<div className="flex items-center justify-end gap-1">
													<Button
														variant="ghost"
														size="icon"
														className="h-7 w-7"
														onClick={(e) => {
															e.stopPropagation();
															navigate(`/wallet/${wallet.id}`);
														}}
														title="View wallet"
													>
														<Eye size={14} />
													</Button>
													<Button
														variant="ghost"
														size="icon"
														className="h-7 w-7"
														onClick={(e) => {
															e.stopPropagation();
															const allAddresses = collectAllAddresses(wallet);
															if (allAddresses) {
																copyToClipboard(allAddresses, "All addresses");
															} else {
																toast.error("No addresses to copy");
															}
														}}
														title="Copy all addresses"
													>
														<ClipboardCopy size={14} />
													</Button>
												</div>
											</TableCell>
										</TableRow>
									))
								)}
							</TableBody>
						</Table>
					</div>
				)}

				{/* Results count */}
				<p className="mt-3 text-xs text-(--color-muted-foreground)">
					Showing {filteredWallets.length} of {stellarWallets.length} Stellar
					wallets
				</p>
			</div>
		</div>
	);
};

export default StellarAdmin;
